import { useMemo, useSyncExternalStore } from 'react';
import { useAppSelector } from './redux';
import {
  activeSegmentWindow,
  findActiveApiLeg,
  legPathSegmentIds,
  phaseLabel,
} from '../utils/simulationPhaseUi';
import { isLoadedAtTime } from '../utils/simulationPlayback';
import { buildTrackGraph } from '../utils/simulationGraph';
import { getExportableTrackEdges } from '../utils/simulationTrackNetworkView';
import { findNearestRailForOHT } from '../utils/topology';
import { labelOhtRole, parseOhtRole } from '../features/simulation/ohtDedicatedEndpoints';
import { computeOhtUtilizationPct } from '../utils/ohtFleetMetrics';
import { simulationClockStore } from '../store/simulationClockStore';
import { liveOhtRuntimeStore } from '../store/liveOhtRuntimeStore';
import { liveMotionStore } from '../store/liveMotionStore';
import type { LayoutElement } from '../types';
import type { AmhsFinalSimulationLeg, BrowserKinetic } from '../types/simulationPlayback';
import type { TransportRequest } from '../types/simulationPlayback';

export type SelectedOhtStatus =
  | 'IN TRANSIT'
  | 'DEADHEAD'
  | 'WAITING'
  | 'IDLE'
  | 'HOME'
  | 'CLEARANCE';

export type OhtFleetOption = {
  id: string;
  name: string;
  status: SelectedOhtStatus;
  utilizationPct: number | null;
  label: string;
};

export type OhtSimulationSnapshot = {
  elementId: string;
  displayName: string;
  ohtIds: string[];
  status: SelectedOhtStatus;
  phase: string;
  loaded: boolean;
  location: string;
  destination: string;
  eta: string;
  requestId: string | null;
  foupId: string | null;
  dedicatedRole: string | null;
  toolType: string | null;
  toToolType: string | null;
  routeNodes: Array<{ id: string; label: string; active: boolean }>;
  nextActions: string[];
  activeLeg: AmhsFinalSimulationLeg | null;
  graphicUrl: string;
  utilizationPct: number | null;
};

type OhtSimulationContext = {
  elements: Record<string, LayoutElement>;
  elementList: LayoutElement[];
  simTime: number;
  browserKinetic: BrowserKinetic | null | undefined;
  transportRequests: TransportRequest[];
  liveOhtCargo: Record<string, string>;
  source: 'legacy' | 'live';
  segmentNameById: Map<string, string>;
};

function resolveApiLegs(ctx: OhtSimulationContext): AmhsFinalSimulationLeg[] {
  if (ctx.source === 'live') {
    const liveLegs = liveMotionStore.getApiLegs();
    if (liveLegs.length > 0) return liveLegs;
  }
  return ctx.browserKinetic?.apiLegs ?? [];
}

function resolveSimOhtIds(element: LayoutElement): string[] {
  const props = element.customProperties ?? {};
  const ids = [
    element.id,
    typeof props.amhsId === 'string' ? props.amhsId : '',
    typeof props.ohtId === 'string' ? props.ohtId : '',
    element.metadata.name,
  ]
    .map((s) => String(s || '').trim())
    .filter(Boolean);
  return [...new Set(ids)];
}

function statusFromLeg(leg: AmhsFinalSimulationLeg | null): SelectedOhtStatus {
  if (!leg) return 'IDLE';
  if (leg.phase === 'return_home') return 'HOME';
  if (leg.phase === 'deadhead') return 'DEADHEAD';
  if (leg.phase === 'delivery') return 'IN TRANSIT';
  if (leg.phase === 'process_wait') {
    return leg.reason === 'segmentClearance' ? 'CLEARANCE' : 'WAITING';
  }
  return 'IN TRANSIT';
}

function formatEta(remainingSec: number): string {
  if (!Number.isFinite(remainingSec) || remainingSec < 0) return '—';
  const total = Math.max(0, Math.round(remainingSec));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
}

function shortSegmentLabel(segmentId: string, nameBySegmentId: Map<string, string>): string {
  const named = nameBySegmentId.get(segmentId);
  if (named) return named;
  const parts = segmentId.split(/[:/_-]/);
  const last = parts[parts.length - 1] || segmentId;
  return last.length > 10 ? last.slice(-10) : last;
}

function buildSegmentNameById(elements: Record<string, LayoutElement>): Map<string, string> {
  const map = new Map<string, string>();
  try {
    const layoutList = Object.values(elements);
    const graph = buildTrackGraph(layoutList);
    const exportable = getExportableTrackEdges(graph, layoutList);
    for (const { edge, segmentId } of exportable) {
      const rail = elements[edge.id];
      const name = rail?.metadata.name?.trim();
      if (name) map.set(segmentId, name);
      else map.set(segmentId, segmentId);
    }
  } catch {
    // ignore graph errors when layout is incomplete
  }
  return map;
}

function findActiveLegForOht(
  element: LayoutElement,
  ctx: OhtSimulationContext
): AmhsFinalSimulationLeg | null {
  const ohtIds = resolveSimOhtIds(element);
  if (ohtIds.length === 0) return null;
  const legs = resolveApiLegs(ctx);
  const runtime = ohtIds.map((id) => liveOhtRuntimeStore.getOht(id)).find(Boolean);
  const vehicleTime =
    ctx.source === 'live' && runtime ? runtime.localSimTimeS : ctx.simTime;
  for (const oid of ohtIds) {
    const leg = findActiveApiLeg(legs, oid, vehicleTime);
    if (leg) return leg;
  }
  return null;
}

export function buildOhtFleetOption(
  element: LayoutElement,
  ctx: OhtSimulationContext
): OhtFleetOption {
  const name = element.metadata.name?.trim() || element.id;
  const ohtIds = resolveSimOhtIds(element);
  const status = statusFromLeg(findActiveLegForOht(element, ctx));
  const apiLegs = resolveApiLegs(ctx);
  const utilizationPct = computeOhtUtilizationPct(ohtIds, apiLegs, ctx.simTime);
  return {
    id: element.id,
    name,
    status,
    utilizationPct,
    label: `${name} — ${status.toLowerCase()}`,
  };
}

export function buildOhtSimulationSnapshot(
  element: LayoutElement,
  ctx: OhtSimulationContext
): OhtSimulationSnapshot {
  const ohtIds = resolveSimOhtIds(element);
  const displayName = element.metadata.name || element.id || '—';
  const runtime = ohtIds.map((id) => liveOhtRuntimeStore.getOht(id)).find(Boolean);
  const vehicleTime = ctx.source === 'live' && runtime ? runtime.localSimTimeS : ctx.simTime;
  const activeLeg = findActiveLegForOht(element, ctx);
  const status = statusFromLeg(activeLeg);
  const phase = phaseLabel(activeLeg);
  const cargoFoupId =
    (ctx.source === 'live' ? runtime?.carriedFoupId : null) ??
    ohtIds.map((id) => ctx.liveOhtCargo[id]).find(Boolean) ??
    null;
  const loaded =
    Boolean(cargoFoupId) ||
    (ctx.browserKinetic
      ? ohtIds.some((oid) => isLoadedAtTime(ctx.browserKinetic!.events ?? [], oid, ctx.simTime))
      : false);

  let nearestRailName: string | null = null;
  try {
    nearestRailName =
      findNearestRailForOHT(element, ctx.elementList)?.rail.metadata.name ?? null;
  } catch {
    nearestRailName = null;
  }

  const location =
    activeLeg?.fromPortId ||
    nearestRailName ||
    `X ${element.position.x.toFixed(0)}, Y ${element.position.y.toFixed(0)}`;
  const destination = activeLeg?.toPortId || '—';
  const eta = activeLeg ? formatEta(activeLeg.endSec - vehicleTime) : '—';
  const requestId = activeLeg?.requestId || null;
  const foupId =
    cargoFoupId ??
    (requestId
      ? (() => {
          const tr = ctx.transportRequests.find(
            (r) => r.requestId === requestId || r.transportRequestId === requestId
          );
          return tr?.foupId ? String(tr.foupId) : null;
        })()
      : null);

  const ohtRole = parseOhtRole(element.customProperties?.ohtRole);
  const toolType = String(element.customProperties?.toolType ?? '')
    .trim()
    .toUpperCase();
  const toToolType = String(element.customProperties?.toToolType ?? '')
    .trim()
    .toUpperCase();

  const routeNodes = activeLeg
    ? legPathSegmentIds(activeLeg).map((segmentId) => {
        const win = activeSegmentWindow(activeLeg, ctx.simTime);
        return {
          id: segmentId,
          label: shortSegmentLabel(segmentId, ctx.segmentNameById),
          active: win?.segmentId === segmentId,
        };
      })
    : [];

  const nextActions = (() => {
    if (!activeLeg) return [] as string[];
    const actions: string[] = [];
    if (activeLeg.fromPortId && activeLeg.toPortId) {
      actions.push(`Move ${activeLeg.fromPortId} → ${activeLeg.toPortId}`);
    }
    const path = legPathSegmentIds(activeLeg);
    const win = activeSegmentWindow(activeLeg, ctx.simTime);
    let startIdx = 0;
    if (win?.segmentId) {
      const i = path.indexOf(win.segmentId);
      if (i >= 0) startIdx = i;
    }
    const upcoming = path.slice(startIdx, startIdx + 3);
    for (let i = 0; i < upcoming.length - 1; i++) {
      const a = shortSegmentLabel(upcoming[i]!, ctx.segmentNameById);
      const b = shortSegmentLabel(upcoming[i + 1]!, ctx.segmentNameById);
      actions.push(`Traverse ${a} → ${b}`);
    }
    if (activeLeg.phase === 'delivery' || activeLeg.phase === 'deadhead') {
      actions.push(`Arrive ${activeLeg.toPortId || 'destination'}`);
    }
    return actions.slice(0, 3);
  })();

  return {
    elementId: element.id,
    displayName,
    ohtIds,
    status,
    phase,
    loaded,
    location,
    destination,
    eta,
    requestId,
    foupId,
    dedicatedRole: ohtRole ? labelOhtRole(ohtRole) : null,
    toolType: toolType || null,
    toToolType: toToolType || null,
    routeNodes,
    nextActions,
    activeLeg,
    graphicUrl: loaded ? '/oht_withload.png' : '/oht_withoutload.png',
    utilizationPct: computeOhtUtilizationPct(ohtIds, resolveApiLegs(ctx), ctx.simTime),
  };
}

function useOhtSimulationContext(): OhtSimulationContext {
  const elements = useAppSelector((state) => state.layout.elements);
  const running = useAppSelector((state) => state.simulation.running);
  const reduxTime = useAppSelector((state) => state.simulation.simulationTime);
  const browserKinetic = useAppSelector((state) => state.simulation.browserKinetic);
  const transportRequests = useAppSelector((state) => state.simulation.transportRequests);
  const liveOhtCargo = useAppSelector((state) => state.simulation.live.ohtCargo);
  const source = useAppSelector((state) => state.simulation.source);

  const clockTime = useSyncExternalStore(
    (onStoreChange) => simulationClockStore.subscribe(onStoreChange),
    () => simulationClockStore.getTime(),
    () => 0
  );
  const simTime = running ? clockTime : reduxTime;
  const elementList = useMemo(() => Object.values(elements), [elements]);
  const segmentNameById = useMemo(() => buildSegmentNameById(elements), [elements]);

  return useMemo(
    () => ({
      elements,
      elementList,
      simTime,
      browserKinetic,
      transportRequests,
      liveOhtCargo,
      source,
      segmentNameById,
    }),
    [
      browserKinetic,
      elementList,
      elements,
      liveOhtCargo,
      segmentNameById,
      simTime,
      source,
      transportRequests,
    ]
  );
}

export function useOhtFleetSnapshots() {
  const ctx = useOhtSimulationContext();
  useAppSelector((state) => state.simulation.simulationTime);

  const fleetElements = useMemo(
    () =>
      ctx.elementList
        .filter((element) => element.type === 'oht-vehicle' && element.metadata.visible !== false)
        .sort((a, b) =>
          (a.metadata.name || a.id).localeCompare(b.metadata.name || b.id, undefined, {
            numeric: true,
          })
        ),
    [ctx.elementList]
  );

  const fleetOptions = useMemo(
    () => fleetElements.map((element) => buildOhtFleetOption(element, ctx)),
    [ctx, fleetElements]
  );

  const snapshots = useMemo(
    () => fleetElements.map((element) => buildOhtSimulationSnapshot(element, ctx)),
    [ctx, fleetElements]
  );

  return { fleetOptions, snapshots, simTime: ctx.simTime };
}

/**
 * Live selected-OHT snapshot for OHT Route panel (canvas selection).
 */
export function useSelectedOhtSimulation() {
  const ctx = useOhtSimulationContext();
  const selectedIds = useAppSelector((state) => state.selection.selectedIds);
  const { fleetOptions } = useOhtFleetSnapshots();

  const selected = selectedIds.length === 1 ? (ctx.elements[selectedIds[0] ?? ''] ?? null) : null;
  const isOht = selected?.type === 'oht-vehicle';

  const snapshot =
    selected && isOht ? buildOhtSimulationSnapshot(selected, ctx) : null;

  return {
    selected,
    isOht: Boolean(isOht && selected),
    simTime: ctx.simTime,
    fleetOptions,
    ...(snapshot ?? {
      displayName: '—',
      ohtIds: [],
      status: 'IDLE' as SelectedOhtStatus,
      phase: 'Idle',
      loaded: false,
      location: '—',
      destination: '—',
      eta: '—',
      requestId: null,
      foupId: null,
      dedicatedRole: null,
      toolType: null,
      toToolType: null,
      routeNodes: [],
      nextActions: [],
      activeLeg: null,
      graphicUrl: '/oht_withoutload.png',
      utilizationPct: null,
    }),
  };
}

export function selectedOhtStatusClass(status: SelectedOhtStatus): string {
  switch (status) {
    case 'IN TRANSIT':
      return 'text-sky-600';
    case 'DEADHEAD':
      return 'text-violet-600';
    case 'WAITING':
    case 'CLEARANCE':
      return 'text-amber-600';
    case 'HOME':
      return 'text-slate-500';
    default:
      return 'text-slate-400';
  }
}
